// Default import: also exported by the v2 copy uppy pulls in, so a stale
// node_modules after a branch switch can't crash the app
import io from "socket.io-client";
import * as localStorage from "@utility/storage/local";
import * as sessionStorage from "@utility/storage/session";
import { tokenPayload } from "@utility/storage/auth";
import { baseURL, refreshSession } from "./config";
import {
  afterConnectError,
  afterRenewFailure,
  afterServerClose,
  onForeground,
  retryDelay,
} from "../views/chat/chatConnectionPolicy";

const apiUrl = new URL(baseURL);

const currentToken = () =>
  localStorage.getItem(localStorage.AUTH_TOKEN_KEY) || sessionStorage.getItem(sessionStorage.AUTH_TOKEN_KEY);
const loggedIn = () => !!currentToken();
// Opened by the navbar only for someone who can read chat. The retry, renew and wake-up
// paths below come back only while it's open, so they never connect anyone else
let wanted = false;
const shouldConnect = () => wanted && loggedIn();
// The token the live connection was made with - the server closes it when that one runs out
let tokenSent = null;

// One connection for the whole app. The server serves it under the API prefix.
const socket = io(apiUrl.origin, {
  path: `${apiUrl.pathname.replace(/\/$/, "")}/socket.io`,
  autoConnect: false,
  transports: ["websocket"],
  // Reconnects quickly (about 0.5-1.5 s, backing off to 5 s), still a little spread out so
  // everyone doesn't arrive at once after a deploy. The old 0-10 s first wait, backing off
  // to 30 s, left a chat deaf for up to a minute after any blip or restart
  reconnectionDelay: 1000,
  reconnectionDelayMax: 5000,
  randomizationFactor: 0.5,
  // Read on every (re)connect so it always carries the current token
  auth: (cb) => {
    tokenSent = currentToken();
    cb({ token: tokenSent });
  },
});

// The login lasts minutes and is renewed only when something is refused. The server closes
// a socket whose login has run out, and refuses a handshake with one ("Unauthorized") - a
// tab opened or woken later starts that way. Left there, chat stayed deaf and the person
// showed offline to everyone until a reload. So the socket renews it the way API calls do,
// then connects with the new one - once per login: refused again with a fresh one, it
// isn't the login. One that's still current was refused for good (archived, removed, no
// chat.read) and stops there (chatConnectionPolicy)
const RENEW_WITHIN_MS = 60 * 1000;
const loginRunningOut = () => {
  const exp = tokenPayload(currentToken() || "")?.exp;
  return typeof exp === "number" && (exp * 1000) - Date.now() < RENEW_WITHIN_MS;
};

/* ----- The connection as the chat notice sees it: up or not, since when, whether it has
   been up at all for this login (the first connect gets longer before it's a problem), and
   whether reconnecting stopped for good (refused access, an ended session) and when, for
   the once-a-minute re-check. "Since when" is kept here, not in the notice, so a remount or
   another view never starts the wait again ----- */
let connection = { connected: false, everConnected: false, downSince: Date.now(), problem: null, stoppedAt: 0 };
// The login reconnecting stopped for - a different one (a new login) starts afresh
let stoppedFor = null;
const connectionListeners = new Set();
const setConnection = (patch) => {
  connection = { ...connection, ...patch };
  connectionListeners.forEach((listener) => listener());
};
export const subscribeChatConnection = (listener) => {
  connectionListeners.add(listener);
  return () => connectionListeners.delete(listener);
};
export const getChatConnection = () => connection;
// A new login (or a renewal) since it stopped: whatever stopped it belonged to the old one
const problemNow = () => {
  if (connection.problem && currentToken() && currentToken() !== stoppedFor) {
    setConnection({ problem: null });
  }
  return connection.problem;
};

// Things that may pass (busy, a failed setup, a renewal that couldn't reach the server):
// one try pending at a time, further apart each time (5 s, 10 s, 20 s... up to a minute)
let retryTimer = null;
let retryAttempt = 0;
const clearRetry = () => {
  clearTimeout(retryTimer);
  retryTimer = null;
};

// Connect if there's no connection and trying can still help - a fresh handshake, checked by
// the server from scratch. One that's up is kept; one already opening isn't started twice
const recover = () => {
  const action = onForeground({
    connected: socket.connected,
    hasToken: !!currentToken() && wanted,
    problem: problemNow(),
    sinceRefusedMs: Date.now() - connection.stoppedAt,
  });
  if (action !== "connect") return;
  if (connection.problem === "denied") setConnection({ stoppedAt: Date.now() });
  socket.connect();
};

const retryLater = () => {
  if (retryTimer) return;
  const delay = retryDelay(retryAttempt, Math.random());
  retryAttempt += 1;
  retryTimer = setTimeout(() => {
    retryTimer = null;
    recover();
  }, delay);
};

const stopReconnecting = (problem) => {
  clearRetry();
  stoppedFor = currentToken();
  setConnection({ problem, stoppedAt: Date.now() });
};

// "Try again" on a refused chat: one new connection, checked by the server from scratch
export const retryChatConnection = () => {
  if (socket.connected || connection.problem !== "denied" || !shouldConnect()) return;
  setConnection({ stoppedAt: Date.now() });
  socket.connect();
};

let renewing = null;
// The login a renewal was last made for
let renewedFor = null;
const renewAndConnect = () => {
  if (renewing || !shouldConnect()) return;
  renewedFor = currentToken();
  renewing = refreshSession()
    .then((jwt) => jwt && shouldConnect() && !socket.connected && socket.connect())
    .catch((error) => {
      // Refused: the session is over (the next API call signs out). Unreachable: later
      if (afterRenewFailure(error) === "stop-session") stopReconnecting("session-ended");
      else {
        renewedFor = null;
        retryLater();
      }
    })
    .finally(() => {
      renewing = null;
    });
};

socket.on("connect", () => {
  clearRetry();
  retryAttempt = 0;
  setConnection({ connected: true, everConnected: true, problem: null });
});
socket.on("connect_error", (err) => {
  const action = afterConnectError(err.message, {
    loginRunningOut: loginRunningOut(),
    renewedAlready: !!renewedFor && renewedFor === currentToken(),
  });
  if (action === "retry-later") retryLater();
  else if (action === "renew") renewAndConnect();
  else if (action === "stop-denied") stopReconnecting("denied");
  else if (action === "stop-session") stopReconnecting("session-ended");
});

// The server closed the connection. It says why just before (chat:closing), and a role or
// permission edit says so too (chat:access_changed). An expired login is renewed and
// reconnected at once - inside the server's few seconds' grace, so nobody sees this person
// go offline; access taken away or an ended session stops there; anything else may pass
let accessChanged = false;
let closingReason = null;
socket.on("chat:access_changed", () => {
  accessChanged = true;
});
socket.on("chat:closing", (data) => {
  closingReason = typeof data?.reason === "string" ? data.reason : null;
});
socket.on("disconnect", (reason) => {
  const serverReason = closingReason;
  const wasAccessChange = accessChanged;
  closingReason = null;
  accessChanged = false;
  // Only the first drop starts the wait - more errors in the same outage don't
  if (connection.connected) setConnection({ connected: false, downSince: Date.now() });
  if (reason !== "io server disconnect" || !shouldConnect()) return;
  const action = afterServerClose(serverReason, {
    accessChanged: wasAccessChange,
    loginRunningOut: loginRunningOut(),
  });
  if (action === "reconnect-now") setTimeout(recover, 300 + (Math.random() * 700));
  else if (action === "renew") renewAndConnect();
  else if (action === "retry-later") retryLater();
  else if (action === "stop-denied") stopReconnecting("denied");
  else if (action === "stop-session") stopReconnecting("session-ended");
});

// Before the login the connection carries runs out, it's renewed on the live connection
// (chat:reauth) - so the server doesn't close it and it doesn't re-join and catch up every
// time the login is renewed. The token an API call already renewed is used when there is
// one, else it's renewed now. The server checks it as it does a new connection; refused,
// nothing changes and the close-and-reconnect above takes over when the old one runs out
let reauthTimer = null;
const reauth = async () => {
  if (!socket.connected || !shouldConnect()) return;
  let token = currentToken();
  if (!token || token === tokenSent || loginRunningOut()) token = await refreshSession().catch(() => null);
  if (!token || !socket.connected || !shouldConnect()) return;
  socket.timeout(10000).emit("chat:reauth", { token }, (err, result) => {
    if (err || !result?.ok) return;
    tokenSent = token;
    // eslint-disable-next-line no-use-before-define
    scheduleReauth();
  });
};
const scheduleReauth = () => {
  clearTimeout(reauthTimer);
  const exp = tokenPayload(tokenSent || "")?.exp;
  if (typeof exp !== "number") return;
  reauthTimer = setTimeout(reauth, Math.max((exp * 1000) - Date.now() - RENEW_WITHIN_MS, 0));
};
socket.on("connect", scheduleReauth);

// Whether this browser window is the one in front: a chat open in a window behind another app
// isn't being read, so the server still pushes its messages to the phone (it only holds back
// for a chat someone is looking at). Said again on every new connection
const reportFocus = () => {
  if (socket.connected) {
    socket.emit("chat:visibility", { visible: document.visibilityState === "visible" && document.hasFocus() });
  }
};
socket.on("connect", reportFocus);
window.addEventListener("focus", reportFocus);
window.addEventListener("blur", reportFocus);
document.addEventListener("visibilitychange", reportFocus);
socket.on("disconnect", () => clearTimeout(reauthTimer));

// Network back, or the tab/laptop woken up: connect now rather than wait for the next retry
// (refused access is asked again here too, at most once a minute)
window.addEventListener("online", recover);
document.addEventListener("visibilitychange", () => {
  if (document.visibilityState !== "visible") return;
  // Time hidden doesn't count towards the notice's wait: a connection that died meanwhile
  // gets the same few seconds to come back as any other drop
  if (!connection.connected && !connection.problem) setConnection({ downSince: Date.now() });
  recover();
});

export const openSocket = () => {
  // A first connect for this login: its wait starts now
  if (!wanted && !socket.connected) setConnection({ everConnected: false, downSince: Date.now() });
  wanted = true;
  // A fresh start (a login): whatever stopped reconnecting before no longer applies
  stoppedFor = null;
  if (connection.problem) setConnection({ problem: null });
  socket.connect();
};
export const closeSocket = () => {
  wanted = false;
  clearRetry();
  clearTimeout(reauthTimer);
  socket.disconnect();
};

export default socket;
